import { createRef, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LoginUser } from '../../services/UsersService';
import { listLoginUsers, updateLoginUser as updateLoginUserAction } from '../users/UsersSlice';
import { usersState } from '../users/UsersSlice';
import AnonymizeLoginUser from './anonymizeLoginUser/AnonymizeLoginUser';
import AnonymizeLoginUserModal from './anonymizeLoginUserModal/AnonymizeLoginUserModal';
import MoveLoginUserModal from './moveLoginUserModal/MoveLoginUserModal';
import { open as openMoveLoginUserModal } from './moveLoginUserModal/MoveLoginUserModalSlice';
import { RequireTier } from '../auth/RequireTier';

interface LoginUsersProps {
  readonly userId: number;
}

const LoginUsers = (props: LoginUsersProps) => {
  const state = useAppSelector(usersState);
  const dispatch = useAppDispatch();
  const { userId } = props;
  const loginUserList = useMemo(() => state.loginUsersForUsers[userId] || [], [state.loginUsersForUsers, userId]);
  const [loginUsers, setLoginUsers] = useState<Array<{ id: number; email: string }>>([]);
  const inputRefs = useRef<Map<number, React.RefObject<HTMLInputElement>>>(new Map());

  const setEmail = (id: number, email: string) => {
    setLoginUsers(loginUsers.map((l) => (l.id === id ? { ...l, email } : { ...l })));
  };

  const updateLoginUser = (loginUser: LoginUser) => {
    const email = loginUsers.find((l) => l.id === loginUser.id)?.email;
    if (email) {
      dispatch(
        updateLoginUserAction({
          originalLoginUser: { id: loginUser.id, userId: loginUser.userId },
          loginUser: { email },
        })
      );
    }
  };

  useEffect(() => {
    if (userId) {
      dispatch(listLoginUsers({ userId }));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    setLoginUsers(loginUserList.map((l) => ({ email: l.email, id: l.id })));
    inputRefs.current.forEach((value, key) => {
      const user = loginUserList.find((l) => l.id === key);
      if (user && value.current) {
        value.current.value = user.email;
      }
    });
  }, [loginUserList]);

  const handleOpenLoginUsers = (loginUser: LoginUser) => {
    dispatch(openMoveLoginUserModal(loginUser));
  };

  return (
    <Fragment>
      <AnonymizeLoginUserModal />
      <MoveLoginUserModal />
      <div className="card">
        <div className="card-header">
          <h4 className="card-header-title">Login users</h4>
        </div>
        <div className="card-body">
          {loginUserList.map((loginUser) => {
            const buttonDisabled = loginUsers.find((l) => l.id === loginUser.id)?.email === loginUser.email;
            const inputRef = createRef<HTMLInputElement>();
            inputRefs.current.set(loginUser.id, inputRef);
            return (
              <div key={`loginUser-${loginUser.id}`} className="align-items-center">
                <h2>
                  <b>Id: {loginUser.id}</b>
                </h2>
                <p>Role: {loginUser.role}</p>
                <p>Phone number: {loginUser.phoneNumber || 'N.A.'}</p>
                <div className="d-flex">
                  <input
                    className="form-control"
                    defaultValue={loginUser.email}
                    onChange={(event) => {
                      setEmail(loginUser.id, event.target.value);
                    }}
                    ref={inputRef}
                  />
                  <button
                    className="btn btn-primary ml-3"
                    disabled={buttonDisabled}
                    onClick={() => {
                      updateLoginUser(loginUser);
                    }}
                  >
                    <i className="fe fe-check"></i>
                  </button>
                </div>
                {loginUser.pendingInvitation && <div className="my-3 col alert alert-primary">Pending invitation</div>}
                <div className="form-group">
                  <div className="form-label mb-0">Move login user to another account</div>
                  <RequireTier requiredTier="superadmin" policy="disable">
                    <button className="btn btn-warning" onClick={() => handleOpenLoginUsers(loginUser)}>
                      Move login user
                    </button>
                  </RequireTier>
                </div>
                <AnonymizeLoginUser loginUser={loginUser} />
                <hr />
              </div>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
};

export default LoginUsers;
